import { IntegrationAPI } from '@business/api/integration';
import { IntegrationGroup, IntegrationType } from '@business/entities/integration';
import { IntegrationService, CommonService } from '@business/services';
import { FormInput, FormSelect } from '@core/components/form';
import { formControl, FormDataType, formGroup, Validators } from '@core/utils/form';
import { useSettings } from '@core/utils/hooks/misc/useSettings';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';
import { R } from '@core/utils/r';
import { useLazyEffect } from '@core/utils/react';
import { TaxjarFormData, TaxJarTaxType } from '@modules/settings/SettingsPage/Components/IntegrationsTab/taxjar/TaxjarOverlay';
import React, { memo, useCallback, useMemo, useState, useEffect } from 'react';
import { Col, Collapse, Row } from 'reactstrap';
import { tap } from 'rxjs/operators';
import { QBSalesTaxResponse } from '@business/entities/salesTax';

export function defineTmInvoiceSalesTaxForm() {
  return formGroup({
    isSalesTax: formControl<boolean>({
      initialValue: false,
    }),
    isManual: formControl<boolean>({
      initialValue: false,
    }),
    value: formControl<number>({
      placeholder: 'Value',
      initialValue: 0,
      validators: [Validators.min(0), Validators.max(100)],
    }),
    taxAmount: formControl<number>({}),
    taxCode: formControl<string>({}),
  });
}

export type TmInvoiceSalesTaxForm = ReturnType<typeof defineTmInvoiceSalesTaxForm>;
export type TmInvoiceSalesTaxData = FormDataType<typeof defineTmInvoiceSalesTaxForm>;

export interface SalesTaxLineItems {
  title?: string | null;
  qty?: number | null;
  rate?: number | null;
  taxable?: boolean | null;
}
export interface TmInvoiceSalesTaxProps {
  form: TmInvoiceSalesTaxForm;
  lines?: SalesTaxLineItems[];
  addressId?: string;
  isNew?: boolean;
  items?: QBSalesTaxResponse[];
  disabled?: boolean;
}

export const TmInvoiceSalesTax = memo((props: TmInvoiceSalesTaxProps) => {
  const { form, lines = [], addressId = '0', isNew = false, items = [], disabled = false } = props;
  const formValue = useNonNilObservable(form.value$);
  const settings = useSettings();
  const taxIntegrations = useMemo(() => R.filter(settings?.integrations || [], i => i.group === IntegrationGroup.tax), [settings]);
  const taxJar = R.find(taxIntegrations, i => i.type === IntegrationType.taxjar);
  const taxJarSettings = useMemo(() => taxJar?.settings as TaxjarFormData, [taxJar]);
  const salesTaxType = taxJarSettings?.salesTaxType || TaxJarTaxType.location;
  const [submitting, setSubmitting] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const isASTEnabled = useMemo(() => IntegrationService.isASTEnabled(), []);
  const showNotShowField = useMemo(() => isASTEnabled && isNew, [isASTEnabled, isNew]);
  const saleTaxItems = useMemo(() => CommonService.selectItems(items, i => `${i.name} (${i.rate}%)`, 'id'), [items]);

  useEffect(() => {
    form.controls.value.setConfig({ required: formValue.isSalesTax && lines.length > 0 });
  }, [form.controls.value, formValue.isSalesTax, lines.length]);

  useLazyEffect(() => {
    const total = R.reduce(lines, (sum, l, index) => sum + (!!l.taxable && l.rate && l.qty ? l.rate * l.qty : 0), 0);

    if (salesTaxType === TaxJarTaxType.order && taxJar?.type && addressId && total > 0 && total !== totalAmount && !formValue.isManual) {
      setTotalAmount(total);
      IntegrationAPI.calculateTax(taxJar.type, lines, addressId)
        .pipe(tap((tax: number) => form.patchValue({ value: tax })))
        .pipe(tap(() => setSubmitting(false)))
        .subscribe();
    }
  }, [addressId, form, lines, taxJar, totalAmount, taxJarSettings, salesTaxType]);

  useLazyEffect(() => {
    if (salesTaxType === TaxJarTaxType.location && taxJar?.type && addressId && !formValue.isManual) {
      IntegrationAPI.calculateTax(taxJar.type, [], addressId)
        .pipe(tap((tax: number) => form.patchValue({ value: tax })))
        .pipe(tap(() => setSubmitting(false)))
        .subscribe();
    }
  }, [addressId, form, taxJar, taxJarSettings, salesTaxType]);

  // useLazyEffect(() => {
  //   const isSalesTax = R.filter(lines, l => !!l.taxable).length > 0;
  //   form.patchValue({ isSalesTax });
  // }, [form, lines]);

  const onTaxCalculate = useCallback(
    (type: IntegrationType) => {
      setSubmitting(true);
      return IntegrationAPI.calculateTax(type, lines, addressId)
        .pipe(tap((tax: number) => form.patchValue({ value: tax })))
        .pipe(tap(() => setSubmitting(false)))
        .subscribe();
    },
    [addressId, form, lines],
  );

  // useEffect(() => {
  //   form.patchValue({ isSalesTax });
  //   form.controls.value.setConfig({ required: false });
  // }, [form, isSalesTax]);

  const isFieldDisabled = useMemo(() => isASTEnabled || !R.isEmpty(formValue.taxCode), [formValue.taxCode, isASTEnabled]);

  console.log('formValue', formValue);
  if (!formValue.isSalesTax) {
    return null;
  }

  return (
    <div style={{ textAlign: 'right' }} className="mt-2 mb-2">
      <Collapse isOpen={true}>
        {!showNotShowField && (
          <Row className="mt-2">
            <Col>
              <div className="d-flex align-items-center justify-content-end">
                <div className="form-label mr-3">
                  <label>Sales tax</label>
                </div>
                {saleTaxItems.length > 0 && (
                  <div style={{ flex: 1, width: 250, marginRight: 10 }}>
                    <FormSelect
                      withoutGroup
                      disabled={disabled}
                      control={form.controls.taxCode}
                      items={saleTaxItems}
                      onChange={item => {
                        const selectedTax = items.find(i => i.id === item);
                        form.patchValue({ value: selectedTax?.rate || 0 });
                      }}
                    />
                  </div>
                )}
                <div style={{ flex: 1, maxWidth: 120 }}>
                  <FormInput
                    invalid={formValue.isSalesTax && R.toNumber(formValue.value) <= 0}
                    withoutGroup
                    //type="number"
                    addonText="%"
                    onKeyUp={() => form.patchValue({ isManual: true })}
                    control={form.controls.value}
                    disabled={isFieldDisabled || disabled}
                  />
                </div>
                <div className="input-group-append">
                  <div className="input-group-text">%</div>
                </div>
              </div>
            </Col>
          </Row>
        )}
        <Row>
          <Col>
            {isASTEnabled && <div className="text-muted mt-2">Sales Tax will be calculated by Quickbooks Automated Sales Tax (AST) after saving</div>}
          </Col>
        </Row>
      </Collapse>
    </div>
  );
});
